import { Component, DestroyRef, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TextareaModule } from 'primeng/textarea';
import { finalize, merge } from 'rxjs';

import { AuthService } from '@ls-commons/auth/services';
import {
  CancelSaveMoleculeComponent,
  FormFieldMoleculeComponent,
  LabelAtomComponent,
} from '@ls-commons/components';
import { ASSIGNABLE_ROLES, getRoleLabel } from '@ls-commons/models';

import { UserActionsType, UsersFacade } from '@ls-commons/users/store';
import { ModalService } from '@ls-commons/utils/modals';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputText } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { BaseEditLogo, UploadImagesComponent } from '../upload-images';
@Component({
  selector: 'app-user-edit',
  standalone: true,
  imports: [
    FormFieldMoleculeComponent,
    SelectButtonModule,
    CancelSaveMoleculeComponent,
    LabelAtomComponent,
    ReactiveFormsModule,
    SkeletonModule,
    InputText,
    TextareaModule,
    UploadImagesComponent,
  ],
  templateUrl: './user-edit.component.html',
  styleUrl: './user-edit.component.scss',
})
export class UserEditComponent extends BaseEditLogo implements OnInit {
  userForm!: FormGroup;
  modalService = inject(ModalService);
  destroyRef = inject(DestroyRef);
  usersFacade = inject(UsersFacade);

  isLoading = this.usersFacade.loadingUser;
  isSaving = this.usersFacade.savingUser;
  isNewUser = this.config.data.userId === null;

  assignedInstallationsCount = 0;

  private authService = inject(AuthService);
  currentUser = computed(() => this.authService.currentUser());
  currentUserRole = this.currentUser()?.role;
  currentUserId = this.currentUser()?.id;

  roleOptions = this.buildRoleOptions();

  skipCorporateOptions: { name: string; code: number }[] = [
    { name: 'Sí', code: 1 },
    { name: 'No', code: 0 },
  ];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    readonly fb: FormBuilder
  ) {
    super();

    this.userForm = this.fb.group({
      id: [0],
      nombre: ['', Validators.required],
      apellidos: [''],
      dni: ['', [Validators.pattern(/^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i)]],
      address: [''],
      telephone: [
        '',
        [Validators.required, Validators.pattern(/^[6789]\d{8}$/)],
      ],
      email: ['', [Validators.required, Validators.email]],
      rol: [null, Validators.required],
      observations: [''],
      image_path: [null],
      skip_corporate_view: [false],
    });
  }

  private buildRoleOptions(): { name: string; code: number }[] {
    const currentUserRole = this.currentUserRole;
    if (currentUserRole === undefined) {
      return [];
    }

    return ASSIGNABLE_ROLES.filter(role => role >= currentUserRole).map(role => ({
      name: getRoleLabel(role),
      code: role,
    }));
  }

  ngOnInit() {
    const { installationsData, userId } = this.config.data;
    this.assignedInstallationsCount = installationsData.filter(
      (installation: any) => installation?.assigned?.usuario_id === userId
    ).length;

    merge(
      this.usersFacade.userError$,
      this.usersFacade.updateError$,
      this.usersFacade.addError$
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.usersFacade.clearError())
      )
      .subscribe(({ error, type }) => {
        error && this.modalService.showErrorMessage(error);
        if (type === UserActionsType.C_GET_USER_FAILED) {
          this.ref.close(true);
        }
      });

    if (this.config.data.userId != null) {
      this.usersFacade.getUser(this.config.data.userId);
      this.usersFacade.selectedUser$.subscribe(({ user, loaded }) => {
        if (loaded && user) {
          this.userForm.patchValue(user);

          this.userForm.patchValue({
            rol: user.role,
          });
          this.initLogo((user as any).image_path || (user as any).image, true);
        }
      });
    }

    this.usersFacade.modifiedUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ user, modified }) => {
        if (modified && user) {
          const formData = user as unknown as FormData;

          const nombre = formData.get('nombre');

          this.modalService.showSuccessMessage(
            `Usuario ${nombre} ${this.isNewUser ? 'añadido' : 'modificado'}`
          );
          this.ref.close(true);
        }
      });
  }

  getForm(): FormGroup {
    return this.userForm;
  }

  closeDialog() {
    this.ref.close(false);
  }

  override handleImagesUpdate(event: any): void {
    super.handleImagesUpdate(event, 'image_path');
  }

  saveUser() {
    this.userForm.markAllAsTouched();

    if (this.userForm.valid) {
      const rawValues = this.userForm.getRawValue();
      let formData = new FormData();

      formData.append('usuarioId', rawValues.id.toString());
      formData.append('nombre', rawValues.nombre);
      formData.append('apellidos', rawValues.apellidos || '');
      formData.append('email', rawValues.email);
      formData.append('rol', rawValues.rol.toString());
      formData.append('telephone', rawValues.telephone);
      formData.append('dni', rawValues.dni || '');
      formData.append('address', rawValues.address || '');
      formData.append('observations', rawValues.observations || '');

      formData.append('image_path', rawValues.image_path || '');
      formData.append('skip_corporate_view', rawValues.skip_corporate_view);

      if (this.filesToUpload && this.filesToUpload.length > 0) {
        formData.append('file', this.filesToUpload[0]);
      }

      formData = this.imgReqIsEmpty(formData);

      if (this.isNewUser) {
        this.usersFacade.addUser(formData as any);
      } else {
        this.usersFacade.updateUser(formData as any);
      }
    }
  }
}
